import { BoxOfferType } from "@modules/boxOffer/entities/boxOfferTypes"
import { BoxStaffPermissions } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { BoxSubscriptionDetailsEntity } from "@modules/boxSubscription/entities/boxSubscriptionDetailsEntity"
import { BoxSubscriptionStatus } from "@modules/boxSubscription/gateway/boxSubscriptionApiDto"
import { word } from "@modules/core/utils/i18n"
import { EllipsisMenu } from "@modules/ui/components/ellipsisMenu"
import dayjs from "dayjs"
import { Pause, Percent, Play, SquareMinus } from "lucide-react"
import { MenuItem } from "primereact/menuitem"
import { useTheme } from "styled-components"

type MemberOfferItemSubscriptionMenuProps = {
  subscriptionDetails: BoxSubscriptionDetailsEntity
  setSubscriptionSelected: React.Dispatch<React.SetStateAction<BoxSubscriptionDetailsEntity | null>>
  setIsOfferSupensionVisible: React.Dispatch<React.SetStateAction<boolean>>
  setIsOfferDiscountVisible: React.Dispatch<React.SetStateAction<boolean>>
  handleDeleteSubscription: () => void
} & React.ComponentProps<"div">

export function MemberOfferItemSubscriptionMenu({
  subscriptionDetails,
  setSubscriptionSelected,
  setIsOfferSupensionVisible,
  setIsOfferDiscountVisible,
  handleDeleteSubscription,
  ...props
}: MemberOfferItemSubscriptionMenuProps) {
  const theme = useTheme()
  const canEditMemberOffer = useBoxStaffPermission(BoxStaffPermissions.EDIT_MEMBER_OFFER)

  if (subscriptionDetails.status === BoxSubscriptionStatus.INACTIVE || !canEditMemberOffer) return <> </>

  const isInPause =
    subscriptionDetails.pause?.pausedAt &&
    ((subscriptionDetails.pause.resumeAt && dayjs(subscriptionDetails.pause.resumeAt).isAfter(dayjs())) ||
      !subscriptionDetails.pause.resumeAt)
      ? true
      : false

  const ellipsisItems: MenuItem[] = [
    {
      items: [
        {
          template: () => (
            <span className="flex align-items-center text-white ml-3 py-1">
              {word("box.member.table.offer.item.menu.unavailableBecauseOfSEPA")}
            </span>
          ),
          visible: subscriptionDetails.status === BoxSubscriptionStatus.PROCESSING_PAYMENT,
        },
        {
          label: isInPause
            ? word("box.member.table.offer.item.menu.resumeOffer")
            : word("box.member.table.offer.item.menu.suspendOffer"),
          icon: isInPause ? (
            <Play color={theme.textPrimary} strokeWidth={1} size={16} />
          ) : (
            <Pause color={theme.textPrimary} strokeWidth={1} size={16} />
          ),
          command: () => {
            setSubscriptionSelected(subscriptionDetails)
            setIsOfferSupensionVisible(true)
          },
          visible:
            subscriptionDetails.offer.type === BoxOfferType.Subscription &&
            (subscriptionDetails.status === BoxSubscriptionStatus.ACTIVE ||
              subscriptionDetails.status === BoxSubscriptionStatus.PAUSE ||
              subscriptionDetails.status === BoxSubscriptionStatus.PROCESSING_PAYMENT),
          disabled: subscriptionDetails.status === BoxSubscriptionStatus.PROCESSING_PAYMENT,
        },
        {
          label: subscriptionDetails.coupon
            ? word("box.member.table.offer.item.menu.editDiscount")
            : word("box.member.table.offer.item.menu.applyDiscount"),
          icon: <Percent color={theme.textPrimary} strokeWidth={1} size={16} />,
          command: () => {
            setSubscriptionSelected(subscriptionDetails)
            setIsOfferDiscountVisible(true)
          },
          visible:
            subscriptionDetails.offer.type === BoxOfferType.Subscription &&
            (subscriptionDetails.status === BoxSubscriptionStatus.ACTIVE ||
              subscriptionDetails.status === BoxSubscriptionStatus.PAUSE ||
              subscriptionDetails.status === BoxSubscriptionStatus.PROCESSING_PAYMENT),
          disabled: subscriptionDetails.status === BoxSubscriptionStatus.PROCESSING_PAYMENT,
        },
        {
          label: word("box.member.table.offer.item.menu.removeSubscription"),
          icon: <SquareMinus color={theme.error} strokeWidth={1} size={16} />,
          command: () => {
            setSubscriptionSelected(subscriptionDetails)
            handleDeleteSubscription()
          },
          visible:
            subscriptionDetails.offer.type === BoxOfferType.Subscription ||
            subscriptionDetails.offer.type === BoxOfferType.Millenium,
          disabled: subscriptionDetails.status === BoxSubscriptionStatus.PROCESSING_PAYMENT,
          className: "warning",
        },
      ],
    },
  ]

  return <EllipsisMenu items={ellipsisItems} width={300} {...props} />
}
